import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { cascadesApi, shopsApi, usersApi } from 'api';
import { DataWrapper } from 'components';
import { NEW_ID } from 'constants/common.constants';
import { QueryKey } from 'enums';
import { useUser } from 'hooks';
import { Shop } from 'types';
import { formUtils } from 'utils';

import { ManageShopProvider } from './components';
import { CascadeTab } from './tabs/CascadeTab';
import { GeneralTab } from './tabs/GeneralTab';
import { PayinTab } from './tabs/PayinTab';
import { PayoutTab } from './tabs/PayoutTab';
import { SciTab } from './tabs/SciTab';

export enum ManageShopTab {
  General = 'general',
  Sci = 'sci',
  Payin = 'payin',
  Payout = 'payout',
  Cascade = 'cascade',
}

type Props = {
  shopId: string;
  navigateBack: () => void;
  currentTab: ManageShopTab;
};

export const TabbedManageShop: React.FC<Props> = ({
  shopId,
  navigateBack,
  currentTab,
}) => {
  const { apiRole, isAdminOrTechOperator } = useUser();

  const isNew = useMemo(() => shopId === NEW_ID, [shopId]);

  const queryResult = useQuery(
    [QueryKey.Shops, shopId],
    () => shopsApi.getOneAsRole(apiRole)(shopId),
    {
      enabled: !!shopId && !isNew,
      keepPreviousData: true,
    },
  );

  const shopData = queryResult.data;

  const queryResultTraders = useQuery(
    QueryKey.UsersTraders,
    usersApi.getAllTraders,
    { enabled: isAdminOrTechOperator },
  );

  const queryResultMerchants = useQuery(
    QueryKey.UsersMerchants,
    usersApi.getAllMerchants,
    { enabled: isAdminOrTechOperator },
  );

  const cascadesQuery = useQuery(QueryKey.Cascades, cascadesApi.getAll, {
    enabled: isAdminOrTechOperator,
  });

  const cascadeOptions = useMemo(
    () => formUtils.getOptions(cascadesQuery.data || [], 'name', 'id'),
    [cascadesQuery.data],
  );

  const renderTab = () => {
    switch (currentTab) {
      case ManageShopTab.General:
        return (
          <GeneralTab
            merchantUsers={queryResultMerchants?.data}
            traders={queryResultTraders?.data}
          />
        );
      case ManageShopTab.Sci:
        return <SciTab />;
      case ManageShopTab.Payin:
        return <PayinTab />;
      case ManageShopTab.Payout:
        return <PayoutTab />;
      case ManageShopTab.Cascade:
        return <CascadeTab cascadeOptions={cascadeOptions} />;
    }
  };

  return (
    <DataWrapper
      queryResult={!isNew ? queryResult : undefined}
      ignoreState={{ refetchLoading: true }}
    >
      <div className="tw-max-w-lg">
        <ManageShopProvider
          shopId={shopId}
          shopData={shopData as Shop}
          isNew={isNew}
          navigateBack={navigateBack}
        >
          {renderTab()}
        </ManageShopProvider>
      </div>
    </DataWrapper>
  );
};
